import { useEffect } from 'react';
import { getSelectOptions } from '@components/shared';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import clsx from 'clsx';
import { cloneDeep, get, set, sortBy } from 'lodash-es';

import type { JSONSchema, JSONSchemaProperties, UIChildrenMappingProps } from '../@types/types';

import { getFieldError } from './formatErrors';
import { getComponentType } from './getComponentType';

/** `title` is optional: the mentions-handler currency item clears it — the popover header shows the parent title. */
export type MakeComponentItem = Omit<JSONSchemaProperties, 'title'> & {
  key: string;
  title?: string;
};

const getComponentTypeName = (item: MakeComponentItem): string => {
  const component = item['ui:component'];

  if (component) {
    // Map component names to simpler test ID prefixes
    const componentNameMap: Record<string, string> = {
      InputMultiSelect: 'multiselect',
      InputSelect: 'select',
      InputText: 'text',
      InputNumber: 'number',
      InputCurrency: 'currency',
      InputTextarea: 'textarea',
      InputCalendar: 'calendar',
      InputTime: 'time',
      InputCheckbox: 'checkbox',
      InputCheckboxGroup: 'checkbox-group',
      InputRadioGroup: 'radio-group',
      InputToggleGroup: 'toggle-group',
      InputPercentage: 'percentage',
      InputInteger: 'integer',
      InputSelectApiConnected: 'select-api',
      InputSelectCurrency: 'select-currency',
      InputSelectSearchable: 'select-searchable',
      InputCountrySubDivision: 'country-subdivision',
      AddressAutocomplete: 'address-autocomplete',
      InputMatrix: 'matrix',
      Repeater: 'repeater',
      SectionRepeater: 'section-repeater',
      ToggleableSectionRepeater: 'toggleable-section-repeater',
      Section: 'section',
      Group: 'group',
    };

    return (
      componentNameMap[component] ||
      component
        .toLowerCase()
        .replace(/^input/, '')
        .replaceAll('([A-Z])', '-$1')
        .toLowerCase()
    );
  }

  // Fallback logic matching getComponentType
  if (item.type === 'object' && 'amount' in item.properties && 'currency' in item.properties) {
    return 'currency';
  }

  if (item.type === 'array' && 'items' in item) {
    return 'repeater';
  }

  if (item.type === 'string' && (item.format === 'date-time' || item.format === 'date')) {
    return 'calendar';
  }

  if ('enum' in item && JSON.stringify(item.enum) === JSON.stringify(['Yes', 'No'])) {
    return 'toggle-group';
  }

  if ('enum' in item || 'oneOf' in item) {
    // Check if it's a multi-select (has items with enum)
    if ('items' in item && 'enum' in (item.items as any)) {
      return 'multiselect';
    }
    return 'select';
  }

  if (item.type === 'string') {
    return 'text';
  }

  if (item.type === 'number') {
    return 'number';
  }

  if (item.type === 'object' && !item.title) {
    return 'group';
  }

  if (item.format === 'time') {
    return 'time';
  }

  return 'unknown';
};

const getUIProps = (item: MakeComponentItem) => {
  const OMIT = [
    'ui:autoPopulateFrom',
    'ui:component',
    'ui:component:readonly',
    'ui:className',
    'ui:description',
    'ui:hidden',
    'ui:limitValueLengthPx',
  ];

  const uiKeys = Object.keys(item).filter((key) => key.startsWith('ui:'));
  const uiProps = uiKeys.reduce((acc: Record<string, any>, key: string) => {
    if (!OMIT.includes(key)) {
      const [, propKey] = key.split(':');
      acc[propKey] = get(item, key);
    }
    return acc;
  }, {});

  return uiProps;
};

export interface MapChildrenArgs extends Omit<UIChildrenMappingProps, 'formValues' | 'setFormValues'> {
  isReadOnly?: boolean;
  hideErrors?: boolean;
  onBlur?: () => void;
  parentKey: string;
  parentSchema: JSONSchema;
  useParentKey?: boolean;
  isDisabled?: boolean;
  toggleDependsOn?: string;
}

interface MakeComponentProps extends MapChildrenArgs {
  item: MakeComponentItem;
}

export const MakeComponent = ({
  autoFocus,
  autoGrow,
  ctx,
  isDisabled,
  isReadOnly,
  item,
  hideErrors,
  onBlur,
  onChange,
  parentKey,
  parentSchema,
  showQuestionId,
  showQuestionKey,
  useParentKey,
  validationErrors,
}: MakeComponentProps) => {
  'use memo';
  const Component = getComponentType(item);

  const replace = useDatapointsStore(({ formValues }) =>
    Array.isArray(item.replace) && item.replace.length === 2
      ? [item.replace[0], formValues[item.replace[1]] as string]
      : null,
  );
  const setFormValues = useDatapointsStore(({ setFormValues }) => setFormValues);

  const isHidden = get(item, 'ui:hidden');
  const constantValue = get(item, 'const');

  let ownKey = parentKey === '' ? item.key : [parentKey, item.key].join('.');

  if (useParentKey) {
    ownKey = parentKey;
  }

  useEffect(() => {
    if (constantValue) {
      setFormValues((formValues) => {
        const clonedFormValues = cloneDeep(formValues);
        set(clonedFormValues, ownKey, constantValue);
        return clonedFormValues;
      });
    }
  }, [constantValue, ownKey, setFormValues]);

  if (!Component) {
    return null;
  }
  const itemTitle = replace?.[1] && item.title ? item.title.replace(replace[0], replace[1]) : item.title;
  const isRequired = [...get(parentSchema, ['required'], [])].includes(item.key);
  const uiProps = getUIProps(item);
  const errors = getFieldError(validationErrors, ownKey, item['ui:component']);
  const isSearchable =
    ('enum' in item || 'enum' in get(item, 'items', {}) || 'oneOf' in item || 'oneOf' in get(item, 'items', {})) &&
    item.isSearchable;

  // Construct test ID following pattern: {componentType}:{ownKey}
  const componentType = getComponentTypeName(item);
  const testId = `${componentType}:${ownKey}`;

  return (
    <Component
      autoFocus={autoFocus}
      autoGrow={autoGrow}
      cdrId={item.cdrId}
      cdrName={item.cdrName}
      mrcId={item.mrcId}
      className={clsx('py-2', isHidden && 'hidden', get(item, 'ui:className'))}
      ctx={ctx}
      descriptionText={get(item, 'ui:description')}
      errors={errors}
      iconPosition="right"
      id={item.key}
      data-testid={testId}
      isClearable
      isDisabled={isDisabled}
      isHidden={isHidden}
      isReadOnly={isReadOnly || get(item, 'ui:component:readonly')}
      isRequired={isRequired}
      isSearchable={isSearchable}
      item={item}
      helperText={item.helperText}
      key={item.key}
      labelText={
        (showQuestionId && `${item.qid}. ${itemTitle}`) || (showQuestionKey && <span>{itemTitle}</span>) || itemTitle
      }
      shortName={item?.shortName}
      MakeComponent={MakeComponent}
      name={ownKey}
      hideErrors={hideErrors}
      onBlur={onBlur}
      onChange={onChange}
      options={getSelectOptions(item as JSONSchemaProperties)}
      ownKey={ownKey}
      parentKey={parentKey}
      parentSchema={parentSchema}
      placeholderText={item.placeholderText}
      showQuestionId={showQuestionId}
      showQuestionKey={showQuestionKey}
      validationErrors={validationErrors}
      {...uiProps}
    />
  );
};

const mapChildren = ({
  autoGrow,
  ctx,
  isReadOnly = false,
  hideErrors,
  parentKey,
  parentSchema,
  useParentKey,
  validationErrors,
  showQuestionId,
  showQuestionKey,
  onBlur,
  onChange,
  isDisabled,
}: MapChildrenArgs) => {
  const mappedProperties = Object.keys(parentSchema.properties).map((key) => ({
    ...parentSchema.properties[key],
    key,
  }));
  const sortedProperties = sortBy(mappedProperties, (c) => c.qid);

  return (
    <>
      {sortedProperties.map((item) => (
        <MakeComponent
          autoGrow={autoGrow}
          ctx={ctx}
          isReadOnly={isReadOnly}
          item={item}
          isDisabled={isDisabled}
          key={item.key}
          hideErrors={hideErrors}
          onBlur={onBlur}
          onChange={onChange}
          parentKey={parentKey}
          parentSchema={parentSchema}
          showQuestionId={showQuestionId}
          showQuestionKey={showQuestionKey}
          useParentKey={useParentKey}
          validationErrors={validationErrors}
        />
      ))}
    </>
  );
};

export default mapChildren;
